import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { ApplicationDetail, ApplicationSummary } from "@/api/applications";
import type { RegionCode } from "@/api/filter";
import type {
  RfqFilters,
  SnapshotSelectedOptionInput,
} from "@/api/tasks";
import type { DocManualLineItem } from "@/api/quoteDocuments";

/**
 * Selector workspace state lifted out of FilterPage so the global navbar /
 * floating Request Support button can read the user's current selection
 * (applications + last-configured solution) and render correct counts in
 * the popup. NOT persisted — rehydrates from FilterPage on mount.
 */

/**
 * The solution the RFQ popup should describe: the last one the user opened
 * Configure on, or — when they never opened it — the machine currently selected
 * in the comparison list. `selectedOptions` is only filled once the user submits
 * from inside Configure; otherwise the popup derives the recommended set from
 * `applicationIds` + `requiredMachines` (see useConfigureSelectionPreview).
 */
export interface LastConfiguredState {
  solutionProductId: string;
  solutionName: string | null;
  solutionMachineType: string | null;
  /** Apps scoped to that solution at Configure-open time. */
  applications: ApplicationDetail[];
  /** Ids Configure would query with — may be T-Series A/B scoped, not base ids. */
  applicationIds?: string[];
  selectedOptions: SnapshotSelectedOptionInput[];
  selectedPartNos: string[];
  manualLineItems: DocManualLineItem[];
  region: RegionCode;
  /** Compare-card MSPe auto-feed toggle for this machine (D9). */
  mspAutoFeedEnabled?: boolean;
  /** Comparison cost preview `summary.requiredMachines` for this machine. */
  requiredMachines?: number;
  /**
   * True once the user submitted from inside Configure: `selectedOptions` is
   * then their own selection and must be used verbatim — an empty array means
   * "everything unchecked" (§D.5), never "not derived yet".
   */
  optionsFrozen?: boolean;
  /** Filter chips + compare set at Configure time (POC parity capture for D2). */
  filters?: RfqFilters | null;
}

interface LastConfiguredContextSync {
  solutionProductId: string;
  applications: ApplicationDetail[];
  applicationIds: string[];
  region: RegionCode;
  mspAutoFeedEnabled: boolean;
  requiredMachines: number;
  filters: RfqFilters;
}

function configurationInputsSignature(input: {
  applications: ApplicationDetail[];
  applicationIds?: string[];
  region: RegionCode;
  mspAutoFeedEnabled?: boolean;
  requiredMachines?: number;
}): string {
  return JSON.stringify({
    applicationIds: [...(input.applicationIds ?? [])].sort(),
    applications: [...input.applications]
      .sort((a, b) => a.applicationId.localeCompare(b.applicationId))
      .map((app) => ({
        applicationId: app.applicationId,
        name: app.name,
        applicationNumber: app.applicationNumber,
        oem: app.oem,
        productionProgram: app.productionProgram,
        materialCode: app.materialCode,
        annualVolume: app.annualVolume,
        batchSize: app.batchSize,
        fasteners: [...app.fasteners]
          .sort((a, b) => a.fastenerId.localeCompare(b.fastenerId))
          .map((fastener) => ({
            fastenerId: fastener.fastenerId,
            brand: fastener.brand,
            type: fastener.type,
            qtyPerPart: fastener.qtyPerPart,
            qtyPerPartA: fastener.qtyPerPartA,
            qtyPerPartB: fastener.qtyPerPartB,
          })),
      })),
    region: input.region,
    mspAutoFeedEnabled: input.mspAutoFeedEnabled ?? true,
    requiredMachines: input.requiredMachines ?? 1,
  });
}

export interface SelectorState {
  /** Persisted across the workspace until Clear selection / Reset filters. */
  selectedApps: ApplicationSummary[];
  selectedAppDetails: ApplicationDetail[];
  /** Last solution the user clicked Configure on. Overwritten on each click. */
  lastConfigured: LastConfiguredState | null;
}

const initialState: SelectorState = {
  selectedApps: [],
  selectedAppDetails: [],
  lastConfigured: null,
};

export const selectorSlice = createSlice({
  name: "selector",
  initialState,
  reducers: {
    /** Set the working application list (called when user saves the editor or
     *  picks from "Existing applications"). */
    setSelectorApps: (
      state,
      action: PayloadAction<{
        summaries: ApplicationSummary[];
        details: ApplicationDetail[];
      }>,
    ) => {
      state.selectedApps = action.payload.summaries;
      state.selectedAppDetails = action.payload.details;
    },
    /** Initial write of lastConfigured on Configure-modal open (POC parity). */
    setLastConfigured: (
      state,
      action: PayloadAction<LastConfiguredState | null>,
    ) => {
      state.lastConfigured = action.payload;
    },
    /** Patch the lastConfigured row's selected options/parts/manual items as the
     *  user toggles them inside the Configure modal. */
    patchLastConfigured: (
      state,
      action: PayloadAction<Partial<LastConfiguredState>>,
    ) => {
      if (!state.lastConfigured) return;
      state.lastConfigured = { ...state.lastConfigured, ...action.payload };
    },
    /**
     * Keep the global RFQ popup aligned with the live Selector workspace.
     * Configuration-driving changes invalidate the old frozen option rows so
     * the popup re-derives them; deliberate empty selections remain frozen
     * until one of those inputs actually changes (§D.5 / MD-09). Manual lines
     * are preserved and submitting still leaves the workspace intact
     * (§D.11 / MD-12).
     */
    syncLastConfiguredContext: (
      state,
      action: PayloadAction<LastConfiguredContextSync>,
    ) => {
      const current = state.lastConfigured;
      if (
        !current ||
        current.solutionProductId !== action.payload.solutionProductId
      ) {
        return;
      }

      const configurationChanged =
        configurationInputsSignature(current) !==
        configurationInputsSignature(action.payload);

      current.applications = action.payload.applications;
      current.applicationIds = action.payload.applicationIds;
      current.region = action.payload.region;
      current.mspAutoFeedEnabled = action.payload.mspAutoFeedEnabled;
      current.requiredMachines = action.payload.requiredMachines;
      current.filters = action.payload.filters;

      if (configurationChanged) {
        current.selectedOptions = [];
        current.selectedPartNos = [];
        current.optionsFrozen = false;
      }
    },
    /** Wipe everything (Clear selection / Reset filters). */
    clearSelector: () => initialState,
  },
});

export const {
  setSelectorApps,
  setLastConfigured,
  patchLastConfigured,
  syncLastConfiguredContext,
  clearSelector,
} = selectorSlice.actions;

export default selectorSlice.reducer;
